import React, { useEffect } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useAuth } from "../../context/AuthContext";
import { t } from "@lingui/macro";

const EditUserForm = ({ user, onSave, onCancel, onDelete }) => {
  const { userEmail, userRole } = useAuth();

  useEffect(() => {
    console.log("Logged-in user email from context:", userEmail);
    console.log("Logged-in user role from context:", userRole);
    console.log("User passed to EditUserForm:", user);
  }, [userEmail, userRole, user]);

  if (!user || !userEmail || !userRole) {
    console.log("Error: Invalid user data or not logged in");
    return <div>{t`Error: Invalid user data or not logged in`}</div>;
  }

  const isCurrentUser = user.email === userEmail;
  const isManager = userRole === "ROLE_MANAGER";

  const validationSchema = Yup.object({
    email: Yup.string()
      .email(t`Invalid email`)
      .required(t`Email is required`),
    firstName: Yup.string(),
    lastName: Yup.string(),
    phoneNumber: Yup.string(),
    ...(isManager && { role: Yup.string().required(t`Role is required`) }),
    ...(isCurrentUser && {
      password: Yup.string()
        .min(6, t`Password must be at least 6 characters`)
        .notRequired(),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], t`Passwords must match`)
        .notRequired(),
    }),
  });

  const handleSubmit = (values) => {
    if (!isCurrentUser) {
      const { password, confirmPassword, ...updatedValues } = values;
      onSave(updatedValues);
    } else {
      onSave(values);
    }
  };

  const handleRoleChange = (selectedRole, setFieldValue) => {
    setFieldValue("role", selectedRole);
  };

  return (
    <div className="form-container">
      <h2>{t`Edit User`}</h2>

      <Formik
        initialValues={{
          email: user.email || "",
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          phoneNumber: user.phoneNumber || "",
          role: user.role || "",
          password: "",
          confirmPassword: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, setFieldValue, values }) => (
          <Form>
            {/* Email */}
            <div className="form-row">
              <label htmlFor="email" className="form-label">
                {t`Email`} <span className="required">*</span>
              </label>
              <div className="form-control" style={{ cursor: "default" }}>
                {user.email}
              </div>
              <ErrorMessage
                name="email"
                component="div"
                className="form-control-feedback"
              />
            </div>

            {/* First Name */}
            <div className="form-row">
              <label htmlFor="firstName" className="form-label">
                {t`First Name`}
              </label>
              <Field
                name="firstName"
                type="text"
                className={`form-control ${
                  errors.firstName && touched.firstName ? "is-invalid" : ""
                }`}
              />
              <ErrorMessage
                name="firstName"
                component="div"
                className="form-control-feedback"
              />
            </div>

            {/* Last Name */}
            <div className="form-row">
              <label htmlFor="lastName" className="form-label">
                {t`Last Name`}
              </label>
              <Field
                name="lastName"
                type="text"
                className={`form-control ${
                  errors.lastName && touched.lastName ? "is-invalid" : ""
                }`}
              />
              <ErrorMessage
                name="lastName"
                component="div"
                className="form-control-feedback"
              />
            </div>

            {/* Phone Number */}
            <div className="form-row">
              <label htmlFor="phoneNumber" className="form-label">
                {t`Phone Number`}
              </label>
              <Field
                name="phoneNumber"
                type="text"
                className={`form-control ${
                  errors.phoneNumber && touched.phoneNumber ? "is-invalid" : ""
                }`}
              />
              <ErrorMessage
                name="phoneNumber"
                component="div"
                className="form-control-feedback"
              />
            </div>

            {/* Role (only for managers) */}
            {isManager && (
              <div className="form-row">
                <label htmlFor="role" className="form-label">
                  {t`Role`} <span className="required">*</span>
                </label>
                <select
                  name="role"
                  onChange={(e) =>
                    handleRoleChange(e.target.value, setFieldValue)
                  }
                  className={`form-control ${
                    errors.role && touched.role ? "is-invalid" : ""
                  }`}
                  value={values.role}
                >
                  {/* NIE TAGUJEMY wartości ról */}
                  <option value="WORKER">WORKER</option>
                  <option value="CLIENT">CLIENT</option>
                  <option value="MANAGER">MANAGER</option>
                </select>
                <ErrorMessage
                  name="role"
                  component="div"
                  className="form-control-feedback"
                />
              </div>
            )}

            {/* Password (only for current user) */}
            {isCurrentUser && (
              <>
                <div className="form-row">
                  <label htmlFor="password" className="form-label">
                    {t`New Password`}
                  </label>
                  <Field
                    name="password"
                    type="password"
                    placeholder={t`New password`}
                    className={`form-control ${
                      errors.password && touched.password ? "is-invalid" : ""
                    }`}
                  />
                  <ErrorMessage
                    name="password"
                    component="div"
                    className="form-control-feedback"
                  />
                </div>

                <div className="form-row">
                  <label htmlFor="confirmPassword" className="form-label">
                    {t`Confirm Password`}
                  </label>
                  <Field
                    name="confirmPassword"
                    type="password"
                    placeholder={t`Confirm new password`}
                    className={`form-control ${
                      errors.confirmPassword && touched.confirmPassword
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  <ErrorMessage
                    name="confirmPassword"
                    component="div"
                    className="form-control-feedback"
                  />
                </div>
              </>
            )}

            {/* Actions */}
            <div className="form-actions">
              <button type="submit" className="submit-btn">
                {t`Save`}
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="cancel-btn"
              >
                {t`Cancel`}
              </button>
              {onDelete && (
                <button
                  type="button"
                  onClick={() => onDelete(user.email)}
                  className="delete-btn"
                >
                  🗑️ {t`Delete`}
                </button>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default EditUserForm;
